import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { createPatient } from "../../../../actions/patientActions";
import { backgroundLight, primaryAlt } from "../../../../utils/colors";
import {
  Button,
  Flex,
  Text,
  Box,
  Heading,
  Tooltip,
  Spinner,
  HStack,
  useToast,
  IconButton,
  Icon,
} from "@chakra-ui/react";
import {
  primary,
  white,
  textSecondary,
  buttonRed,
  buttonRedText,
} from "../../../../utils/colors";
import { useTranslation } from "react-i18next";
import FormControlDefault from "../../../../components/input/FormControlDefault";
import FormControlDatePicker from "../../../../components/input/FormControlDatePicker";
import FormControlPhone from "../../../../components/input/FormControlPhone";
// import FormControlAddress from "../../../../components/input/FormControlAddress";
import FormControlCountry from "../../../../components/input/FormControlCountry";
import { calculateAge } from "../../../../utils/calculations";
// import SidePanelHeader from "../../../../components/sidepanel/SidePanelHeader";
import useIsMobile from "../../../../hooks/useIsMobile";
import { motion } from "framer-motion";
import useThemeColors from "../../../../hooks/useThemeColors";
// import noServices from "../../../../assets/images/empty-box.png";
import StatusButtonNew from "../../../../components/buttons/StatusButtonNew";
import { FaArrowLeftLong, FaXmark } from "react-icons/fa6";
import FormControlAddressFull from "../../../../components/input/FormControlAddressFull";
import useDebounce from "../../../../hooks/useDebounce";
import FormControlRepresentativeType from "../../../../components/input/FormControlRepresentativeType";
import FormControlGender from "../../../../components/input/FormControlGender";
import { FaCircleInfo } from "react-icons/fa6";
import { useParams } from "react-router-dom";

const CreatePatientNew = ({ close, tenant, loading }) => {
  //GENERAL
  const { slug } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const toast = useToast();

  //COLORS
  const { whiteMode, newBorderMode, textPrimaryMode, backgroundLightMode } =
    useThemeColors();

  // CONSTANTS
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const debouncedBirthDate = useDebounce(birthDate, 1000); // Add debounce with 500ms delay
  const [personalCode, setPersonalCode] = useState("");
  const [gender, setGender] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneAlt, setPhoneAlt] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState({
    street: "",
    city: "",
    country: "",
    zip: "",
    state: "other",
    display: "",
  });
  const [child, setChild] = useState(false);
  const [status, setStatus] = useState("new");

  const [loadingDisplay, setLoadingDisplay] = useState(true);

  const [tenantAdultAge, setTenantAdultAge] = useState(18);
  const [defaultNewStatusExpiration, setDefaultNewStatusExpiration] =
    useState(14);

  const [representativeFirstName, setRepresentativeFirstName] = useState("");
  const [representativeLastName, setRepresentativeLastName] = useState("");
  const [representativePhone, setRepresentativePhone] = useState("");
  const [representativeEmail, setRepresentativeEmail] = useState("");
  const [representativeType, setRepresentativeType] = useState("parent");

  const [manualAddressVisible, setManualAddressVisible] = useState(false);
  const [showRepresentative, setShowRepresentative] = useState(false);

  // ERRORS
  const isFirstNameError = firstName === "";
  const isLastNameError = lastName === "";
  const isBirthDateError = birthDate === "";
  const isPhoneError = phone.length < 4;
  const isPhoneShortError = phone.length < 6;
  const isGenderError = gender === "";

  const isEmailValidated = !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(
    email
  );

  const isRepresentativeInfoError =
    representativeFirstName === "" ||
    representativeLastName === "" ||
    representativePhone === "" ||
    representativeEmail === "";

  const isAddressError =
    address.street === "" ||
    address.zip === "" ||
    address.city === "" ||
    address.country === "";

  // Email validation: Standard email format
  const isEmailEmpty = email === "";
  const isEmailInvalid =
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email) && !isEmailEmpty;

  // TOOLTIP VALIDATION MESSAGES
  const tooltipMessages = [];

  if (isFirstNameError) tooltipMessages.push(t("formError.firstNameRequired"));
  if (isLastNameError) tooltipMessages.push(t("formError.lastNameRequired"));
  if (isBirthDateError) tooltipMessages.push(t("formError.birthDateRequired"));
  // if (isGenderError) tooltipMessages.push(t("formError.genderRequired"));
  // if (isPhoneShortError) tooltipMessages.push(t("formError.phoneRequired"));
  // if (isAddressError) tooltipMessages.push(t("formError.addressRequired"));
  // if (child && isRepresentativeInfoError)
  //   tooltipMessages.push(t("formError.representativeInformationRequired"));

  // Email error messages
  // if (isEmailEmpty) {
  //   tooltipMessages.push(t("formError.emailRequired"));
  // } else if (isEmailInvalid) {
  //   tooltipMessages.push(t("formError.emailInvalid"));
  // }

  // FUNCTIONS

  // Check if the full date is valid (year, month, and day)
  const isFullDateEntered = (dateString) => {
    const date = new Date(dateString);
    return (
      date instanceof Date &&
      !isNaN(date) &&
      date.getFullYear().toString().length === 4 // Ensure full year is entered
    );
  };

  const handleGenderChange = (selectedOption) => {
    setGender(selectedOption.value);
  };

  const handleRepresentativeTypeChange = (selectedOption) => {
    setRepresentativeType(selectedOption.value);
  };

  const handleAddressSelect = (selectedAddress) => {
    setAddress({
      street: selectedAddress.street,
      city: selectedAddress.city,
      country: selectedAddress.country,
      zip: selectedAddress.zip,
      state: selectedAddress.state,
      display: `${selectedAddress.street}, ${selectedAddress.city}, ${selectedAddress.zip}`,
    });
  };

  // HANDLERS

  const toggleManualAddressVisible = () => {
    setManualAddressVisible(!manualAddressVisible);
  };

  const closeHandler = () => {
    close();
    dispatch({ type: "CREATE_PATIENT_RESET" });
  };

  // SUBMIT

  const submitHandler = (e) => {
    e.preventDefault();

    dispatch(
      createPatient(
        firstName,
        lastName,
        birthDate,
        personalCode,
        gender || "other",
        phone,
        phoneAlt,
        email,
        address.street,
        address.zip,
        address.city,
        address.country,
        address.state,
        child,
        status,
        defaultNewStatusExpiration,
        representativeFirstName,
        representativeLastName,
        representativePhone,
        representativeEmail,
        representativeType,
        slug
      )
    );
  };

  // USE EFFECT

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoadingDisplay(false);
    }, 500); // CHANGE THIS TO HAVE A LONGER LOADING TIME

    // Cleanup function to clear the timeout if the component unmounts
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (tenant) {
      setTenantAdultAge(tenant.adultAge);
      setDefaultNewStatusExpiration(tenant.defaultNewStatusExpiration);
    }
  }, [tenant]);

  // useEffect(() => {
  //   if (address.display === "") {
  //     setAddress({
  //       street: "",
  //       city: "",
  //       country: "",
  //       zip: "",
  //       state: "",
  //       display: "",
  //     });
  //   }
  // }, [address.display]);

  // const handleAddressBlur = () => {
  //   if (address.display === "") {
  //     setAddress({
  //       street: "",
  //       city: "",
  //       country: "",
  //       zip: "",
  //       state: "",
  //       display: "",
  //     });
  //   }
  // };

  useEffect(() => {
    if (debouncedBirthDate && isFullDateEntered(debouncedBirthDate)) {
      const age = calculateAge(debouncedBirthDate);
      setChild(age < tenantAdultAge); // Set child to true if the patient is under the adult age
      setShowRepresentative(age < tenantAdultAge); // Show representative if the patient is under the adult age
    } else {
      setShowRepresentative(false); // Hide if the date isn't fully entered or valid
    }
  }, [debouncedBirthDate, tenantAdultAge]);

  // BIRTHDATE
  const minBirthDate = new Date(1900, 0, 1); // Set minimum date to Jan 1, 1900
  const maxBirthDate = new Date(); // Set maximum date to today

  // LOGS

  // console.log("firstName:", firstName);
  // console.log("lastName:", lastName);
  // console.log("birthDate:", birthDate);
  // console.log("personalCode:", personalCode);
  // console.log("gender", gender);
  // console.log("phone:", phone);
  // console.log("phoneAlt:", phoneAlt);
  // console.log("email:", email);
  // console.log("address:", address);
  // console.log("child:", child);
  // console.log("status", status);

  // console.log("representativeFirstName:", representativeFirstName);
  // console.log("representativeLastName:", representativeLastName);
  // console.log("representativePhone:", representativePhone);
  // console.log("representativeEmail:", representativeEmail);
  // console.log("representativeType:", representativeType);

  // console.log("gender", gender);
  // console.log("phone", phone);

  // console.log("address", address);

  const isMobile = useIsMobile();

  return (
    <>
      {loading || loadingDisplay ? (
        <Flex bg={whiteMode} align="center" justify="center" w="100%" h="100%">
          <Spinner color={primary} size="lg" />
        </Flex>
      ) : (
        <Box
          position="relative"
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.2 } }}
          w="100%"
          h="100%"
        >
          <form
            onSubmit={submitHandler}
            style={{
              height: "100%",
            }}
          >
            <Flex w="100%" h="100%" direction="column">
              {/* HEADER START */}
              <Flex
                justify="space-between"
                align="center"
                p={["1.25rem 1.5rem 1.25rem", "1.25rem 2rem 1.25rem"]}
                borderBottom={`1px solid ${newBorderMode}`}
                mb="0rem"
                position="relative"
                zIndex={90}
                w="100%"
              >
                <Flex align="center">
                  {!isMobile && (
                    <IconButton
                      bg={backgroundLightMode}
                      border={`1px solid ${backgroundLightMode}`}
                      color={textSecondary}
                      icon={<FaArrowLeftLong />}
                      borderRadius="10.75rem"
                      fontSize="0.875rem"
                      onClick={closeHandler}
                      mr="1rem"
                      _hover={{
                        bg: primaryAlt,
                        color: white,
                        border: primaryAlt,
                      }}
                    />
                  )}

                  <Flex align="center">
                    <Heading
                      fontSize={["1rem", "1.125rem"]}
                      color={textPrimaryMode}
                      fontWeight="600"
                      mr={4}
                    >
                      {t("patient.createPatient")}
                    </Heading>
                    {/* {status} */}
                  </Flex>
                </Flex>

                <Flex
                  align="center"
                  justify="flex-end"
                  gap={["0.5rem", "1rem"]}
                >
                  {!isMobile && status === "new" && (
                    <Tooltip
                      p="0.5rem 1rem"
                      borderRadius="1rem"
                      label={
                        <Flex
                          direction="column"
                          justify="center"
                          align="center"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight="500"
                            textAlign="center"
                          >
                            {t("patient.newStatusExplanation")}
                          </Text>
                        </Flex>
                      }
                    >
                      <Flex align="center">
                        <Icon
                          as={FaCircleInfo}
                          fontSize="0.875rem"
                          color={primaryAlt}
                        />
                      </Flex>
                    </Tooltip>
                  )}
                  <StatusButtonNew
                    isSmall
                    currentStatus={status}
                    size={["sm", "md"]}
                    p={["0 1rem", "0 2rem"]}
                    fontSize={["0.75rem", "0.875rem"]}
                    options="createPatient"
                    onUpdateStatus={setStatus}
                  />

                  {isMobile && (
                    <IconButton
                      position="relative"
                      // top="1.5rem"
                      // right="1.5rem"
                      size="sm"
                      bg={backgroundLight}
                      color={textSecondary}
                      onClick={closeHandler}
                      borderRadius="10rem"
                      _hover={{ bg: primaryAlt, color: white }}
                    >
                      <Icon as={FaXmark} />
                    </IconButton>
                  )}
                </Flex>
              </Flex>
              {/* HEADER END */}
              {/* MAIN START */}
              <Flex
                w="100%"
                h="100%"
                direction="column"
                overflowY={["auto", "auto"]}
                p={["1rem 0rem 2rem", "1rem 2rem 7rem"]}
              >
                {/* PATIENT DETAILS START */}
                <Flex direction="column" w="100%">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                    p={["0 1.5rem 0 1.5rem", "initial"]}
                  >
                    {t("patient.patient")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius={["0rem", "1rem"]}
                    p="1.25rem 1.5rem 0.25rem"
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlDefault
                        autoFocus
                        type="text"
                        size="lg"
                        labelFontSize={"0.8rem"}
                        isRequired
                        label={t("form.firstName")}
                        value={firstName}
                        placeholder={t("placeholder.firstName")}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                        }}
                        isError={isFirstNameError}
                        w={["100%", "50%", "50%"]}
                        errorMessage={t("formError.firstNameRequired")}
                      />

                      <FormControlDefault
                        type="text"
                        size="lg"
                        labelFontSize="0.8rem"
                        isRequired
                        label={t("form.lastName")}
                        value={lastName}
                        placeholder={t("placeholder.lastName")}
                        onChange={(e) => {
                          setLastName(e.target.value);
                        }}
                        isError={isLastNameError}
                        w={["100%", "50%", "50%"]}
                        errorMessage={t("formError.lastNameRequired")}
                      />
                    </Flex>

                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlDatePicker
                        type="date"
                        size="lg"
                        labelFontSize="0.8rem"
                        isRequired
                        label={t("form.birthDate")}
                        minDate={minBirthDate}
                        maxDate={maxBirthDate}
                        // value={birthDate}
                        // value="1970-01-01T00:00:34.357Z"
                        placeholder={t("placeholder.birthDate")}
                        onChange={(newValue) => {
                          setBirthDate(newValue);
                        }}
                        onOpen={() => {}}
                        isError={isBirthDateError}
                        w={["100%", "50%", "50%"]}
                        errorMessage={t("formError.birthDateRequired")}
                        zIndex={90005}
                      />

                      <FormControlDefault
                        type="text"
                        size="lg"
                        labelFontSize="0.8rem"
                        // isRequired
                        label={t("form.personalCode")}
                        value={personalCode}
                        placeholder={t("placeholder.personalCode")}
                        onChange={(e) => {
                          setPersonalCode(e.target.value);
                        }}
                        // isError={isPersonalCodeError}
                        w={["100%", "50%", "50%"]}
                        // errorMessage={t("formError.personalCodeRequired")}
                      />
                    </Flex>

                    <Flex
                      w="100%"
                      direction={["column", "row", "row"]}
                      pr="2rem"
                    >
                      <FormControlGender
                        value={gender}
                        // isRequired
                        label={t("patient.gender")}
                        labelFontSize="0.8rem"
                        placeholder={t("placeholder.gender")}
                        onChange={handleGenderChange}
                        w={["100%", "100%", "50%"]}
                        zIndex={90004}
                      />
                    </Flex>
                  </Flex>
                </Flex>
                {/* PATIENT DETAILS END */}
                {/* REPRESENTATIVE DETAILS START */}
                {showRepresentative && (
                  <Flex
                    direction="column"
                    w="100%"
                    mt="1.5rem"
                    as={motion.div}
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={{
                      opacity: 1,
                      scaleY: 1,
                      transition: { duration: 0.2 },
                    }}
                    style={{ transformOrigin: "top" }}
                  >
                    <Flex align="center" m="0 0 0.5rem">
                      <Text
                        fontSize="0.875rem"
                        fontWeight="600"
                        color={textPrimaryMode}
                        mr={2}
                        p={["0 0rem 0 1.5rem", "initial"]}
                      >
                        {t("patient.representativeInfo")}
                      </Text>

                      {!isMobile && (
                        <Tooltip
                          p="0.5rem 1rem"
                          borderRadius="1rem"
                          placement="top"
                          label={
                            <Flex direction="column" align="center">
                              <Text fontSize="xs" fontWeight="500">
                                {t("patient.representativeInfoTooltip")}
                              </Text>
                            </Flex>
                          }
                        >
                          <Tooltip
                            p="0.5rem 1rem"
                            borderRadius="1rem"
                            placement="top"
                            label={
                              <Flex
                                direction="column"
                                align="center"
                                justify="center"
                              >
                                <Text fontSize="xs" fontWeight="500">
                                  {t("patient.representativeInfoTooltip")}
                                </Text>
                              </Flex>
                            }
                          >
                            <Flex align="center">
                              <Icon
                                as={FaCircleInfo}
                                fontSize="0.875rem"
                                color={primaryAlt}
                              />
                            </Flex>
                          </Tooltip>
                        </Tooltip>
                      )}
                    </Flex>
                    <Flex
                      direction="column"
                      w="100%"
                      bg={backgroundLight}
                      borderRadius={["0rem", "1rem"]}
                      p="1.25rem 1.5rem"
                    >
                      <Flex w="100%" align="center" justify="space-between">
                        <FormControlRepresentativeType
                          // isRequired
                          value={representativeType}
                          label={t("patient.representativeType")}
                          placeholder={t("placeholder.representativeType")}
                          onChange={handleRepresentativeTypeChange}
                        />
                      </Flex>

                      <Flex
                        gap={["0rem", "2rem", "2rem"]}
                        w="100%"
                        direction={["column", "row", "row"]}
                      >
                        <FormControlDefault
                          type="text"
                          size="lg"
                          labelFontSize="0.8rem"
                          // isRequired
                          label={t("patient.representativeFirstName")}
                          value={representativeFirstName}
                          placeholder={t("placeholder.representativeFirstName")}
                          onChange={(e) => {
                            setRepresentativeFirstName(e.target.value);
                          }}
                          // isError={isRepresentativeFirstNameError}
                          w={["100%", "50%", "50%"]}
                          errorMessage={t(
                            "formError.representativeFirstNameRequired"
                          )}
                        />

                        <FormControlDefault
                          type="text"
                          size="lg"
                          labelFontSize="0.8rem"
                          // isRequired
                          label={t("patient.representativeLastName")}
                          value={representativeLastName}
                          placeholder={t("placeholder.representativeLastName")}
                          onChange={(e) => {
                            setRepresentativeLastName(e.target.value);
                            // setLastNameTouched(true);
                          }}
                          // isError={isRepresentativeLastNameError}
                          w={["100%", "50%", "50%"]}
                          errorMessage={t(
                            "formError.representativeLastNameRequired"
                          )}
                        />
                      </Flex>

                      <Flex
                        gap={["0rem", "2rem", "2rem"]}
                        w="100%"
                        direction={["column", "row", "row"]}
                      >
                        <FormControlPhone
                          label={t("patient.representativePhone")}
                          size="lg"
                          labelFontSize="0.8rem"
                          value={representativePhone}
                          onChange={(newPhone) => {
                            setRepresentativePhone(newPhone);
                            // setPhoneTouched(true);
                          }}
                          // isRequired={true}
                          // isError={isRepresentativePhoneError}
                          errorMessage={t(
                            "formError.representativePhoneRequired"
                          )}
                          w={["100%", "50%", "50%"]}
                          defaultCountry="lt"
                          zIndex={999999}
                        />

                        <FormControlDefault
                          type="email"
                          size="lg"
                          labelFontSize="0.8rem"
                          // isRequired
                          label={t("patient.representativeEmail")}
                          value={representativeEmail}
                          placeholder={t("placeholder.representativeEmail")}
                          onChange={(e) => {
                            setRepresentativeEmail(e.target.value);
                            // setEmailTouched(true);
                          }}
                          // isTouched={emailTouched}
                          // isError={isRepresentativeEmailError}
                          w={["100%", "50%", "50%"]}
                          errorMessage={t(
                            "formError.representativeEmailRequired"
                          )}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                )}
                {/* REPRESENTATIVE DETAILS END */}
                {/* PATIENT ADDRESS START */}
                <Flex direction="column" w="100%" mt="1.5rem">
                  <Text
                    fontSize="0.875rem"
                    fontWeight="600"
                    color={textPrimaryMode}
                    m="0 0 0.5rem"
                    p={["0 0rem 0 1.5rem", "initial"]}
                  >
                    {isMobile
                      ? t("patient.addressContactDetailsMobile")
                      : t("patient.addressContactDetails")}
                  </Text>
                  <Flex
                    direction="column"
                    w="100%"
                    bg={backgroundLight}
                    borderRadius={["0rem", "1rem"]}
                    p="1.25rem 1.5rem"
                  >
                    <Flex
                      gap={["0rem", "2rem", "2rem"]}
                      w="100%"
                      direction={["column", "row", "row"]}
                    >
                      <FormControlPhone
                        label={t("form.phone")}
                        size="lg"
                        labelFontSize="0.8rem"
                        value={phone}
                        onChange={(newPhone) => {
                          setPhone(newPhone);
                        }}
                        // isRequired={true}
                        isError={isPhoneError}
                        errorMessage={t("formError.phoneRequired")}
                        w={["100%", "50%", "50%"]}
                        defaultCountry="lt"
                        zIndex={90001}
                      />

                      <FormControlPhone
                        label={t("form.phoneAlt")}
                        size="lg"
                        labelFontSize="0.8rem"
                        value={phoneAlt}
                        onChange={(newPhone) => {
                          setPhoneAlt(newPhone);
                        }}
                        isRequired={false}
                        errorMessage={t("formError.phoneRequired")}
                        w={["100%", "50%", "50%"]}
                        defaultCountry="lt"
                        zIndex={90000}
                      />
                    </Flex>

                    <FormControlDefault
                      type="email"
                      size="lg"
                      labelFontSize="0.8rem"
                      // isRequired
                      label={t("form.email")}
                      value={email}
                      placeholder={t("placeholder.email")}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                      isError={isEmailValidated}
                      w="100%"
                      errorMessage={t("formError.emailRequired")}
                    />

                    {!manualAddressVisible && (
                      <Flex direction="column" w="100%" p="0rem">
                        <FormControlAddressFull
                          label={t("form.address")}
                          size="lg"
                          labelFontSize="0.8rem"
                          placeholder={t("placeholder.address")}
                          onAddressSelect={handleAddressSelect}
                          // isRequired={true}
                          currentValue={address.display}
                          w="100%"
                          googleApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                          // onBlur={handleAddressBlur}
                          zIndex={10000}
                        />
                        <Flex
                          align="center"
                          justify="center"
                          gap="1rem"
                          p="0.5rem 0 0"
                        >
                          <Flex
                            position="relative"
                            m="0rem 0 0"
                            justify="flex-end"
                            w="100%"
                          >
                            <Button
                              bg="transparent"
                              onClick={toggleManualAddressVisible}
                              color={primary}
                              fontSize="xs"
                              size="xs"
                              fontWeight="500"
                              p="0.25rem 0.75rem"
                              borderRadius="10rem"
                              opacity="1"
                              transition="all 0.3s"
                              _hover={{
                                bg: white,
                                opacity: "1",
                              }}
                            >
                              {t("onboarding.enterAddressManual")}
                            </Button>
                          </Flex>
                        </Flex>
                      </Flex>
                    )}

                    {manualAddressVisible && (
                      <>
                        <Flex
                          direction="column"
                          align="center"
                          justify="center"
                          p="0rem"
                        >
                          <HStack gap={4} w="100%">
                            <FormControlDefault
                              type="text"
                              size="lg"
                              labelFontSize="0.8rem"
                              // isRequired={true}
                              label={t("form.street")}
                              value={address.street}
                              placeholder={t("placeholder.street")}
                              onChange={(e) =>
                                setAddress({
                                  ...address,
                                  street: e.target.value,
                                })
                              }
                              w={["100%", "50%", "50%"]}
                            />

                            <FormControlDefault
                              type="text"
                              // isRequired={true}
                              size="lg"
                              labelFontSize="0.8rem"
                              label={t("form.zip")}
                              value={address.zip}
                              placeholder={t("placeholder.zip")}
                              onChange={(e) =>
                                setAddress({ ...address, zip: e.target.value })
                              }
                              w={["100%", "50%", "50%"]}
                            />
                          </HStack>

                          <HStack gap={4} w="100%">
                            <FormControlDefault
                              type="text"
                              // isRequired={true}
                              size="lg"
                              labelFontSize="0.8rem"
                              label={t("form.city")}
                              value={address.city}
                              placeholder={t("placeholder.city")}
                              onChange={(e) =>
                                setAddress({ ...address, city: e.target.value })
                              }
                              w={["100%", "50%", "50%"]}
                              errorMessage={t("formError.cityRequired")}
                            />

                            <FormControlCountry
                              label={t("form.country")}
                              size="lg"
                              labelFontSize="0.8rem"
                              value={address.country}
                              // isRequired={true}
                              onChange={(selectedOption) => {
                                setAddress(
                                  Object.assign({}, address, {
                                    country: selectedOption.value,
                                  })
                                );
                              }}
                              w={["100%", "50%", "50%"]}
                              errorMessage={t("formError.countryRequired")}
                              m={["0 0 1rem", "initial"]}
                            />
                          </HStack>
                          <Flex align="center" justify="center" gap="1rem">
                            <Flex
                              position="relative"
                              m="0rem 0 0"
                              justify="center"
                            >
                              <Button
                                bg="transparent)"
                                onClick={toggleManualAddressVisible}
                                color={primary}
                                variant="link"
                                fontSize="xs"
                                fontWeight="500"
                                p="0.25rem 0.75rem"
                                borderRadius="10rem"
                                _hover={{
                                  bg: white,
                                }}
                              >
                                {t("onboarding.enterAddressGoogle")}
                              </Button>
                            </Flex>
                          </Flex>
                        </Flex>
                      </>
                    )}
                  </Flex>
                </Flex>
                {/* PATIENT ADDRESS END */}

                {/* MAIN END */}

                {/* BOTTOM START */}
                <Flex
                  align="center"
                  justify="flex-end"
                  position={["relative", "fixed"]}
                  bottom={["initial", "2rem"]}
                  right={["initial", "2rem"]}
                  gap="0.5rem"
                  zIndex={99999999}
                  p={["2rem 1.5rem 8rem", "initial"]}
                >
                  <Tooltip
                    bg={buttonRed}
                    color={buttonRedText}
                    padding="0.5rem 1rem"
                    borderRadius="1rem"
                    placement="top"
                    label={
                      tooltipMessages.length > 0 ? (
                        <Flex
                          direction="column"
                          justify="center"
                          align="center"
                        >
                          {tooltipMessages.map((message, index) => (
                            <Text
                              key={index}
                              color={buttonRedText}
                              fontSize="xs"
                              fontWeight="500"
                              textAlign="center"
                            >
                              {message}
                            </Text>
                          ))}
                        </Flex>
                      ) : null
                    }
                    isDisabled={tooltipMessages.length === 0} // Disable tooltip if no errors
                  >
                    <Button
                      type="submit"
                      minWidth={["100%", "10rem"]}
                      bg={textPrimaryMode}
                      size="lg"
                      h={["3.5rem", "3rem"]}
                      color={whiteMode}
                      fontSize="sm"
                      borderRadius="10rem"
                      border={`1px solid ${whiteMode}`}
                      _hover={{
                        bg: primaryAlt,
                        color: white,
                        border: `1px solid ${whiteMode}`,
                      }}
                      onClick={submitHandler}
                      isLoading={loading}
                      isDisabled={tooltipMessages.length > 0} // Disable if any errors exist
                    >
                      {t("patient.createPatient")}
                    </Button>
                  </Tooltip>
                </Flex>
              </Flex>
              {/* BOTTOM END */}
            </Flex>
          </form>
        </Box>
      )}
    </>
  );
};

export default CreatePatientNew;
